import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import {
  searchParcels,
  submitDesign,
  getMyDesign,
  updateMyDesign,
} from "../lib/architectPortalB";
import { uploadFile } from "../lib/applications";
import { UploadedRow } from "./RegisterArchitect";
import { Toaster, toast } from "sonner";
import BackBar from "../components/BackBar";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Upload,
  Search,
  AlertCircle,
  Lightbulb,
  Eye,
} from "lucide-react";

const TYPOLOGIES = [
  { key: "single_family", label: "Single-Family" },
  { key: "adu", label: "ADU" },
  { key: "duplex", label: "Duplex" },
  { key: "cottage_missing_middle", label: "Cottage" },
  { key: "missing_middle", label: "Missing Middle" },
  { key: "adaptive_reuse", label: "Adaptive Reuse" },
];

const STYLES = [
  { key: "bungalow", label: "Bungalow" },
  { key: "foursquare", label: "Foursquare" },
  { key: "cottage_court", label: "Cottage Court" },
  { key: "modern", label: "Modern Infill" },
  { key: "craftsman", label: "Craftsman" },
  { key: "shed_modern", label: "Shed Modern" },
  { key: "traditional", label: "Traditional" },
];

const ZONING = ["D-5", "D-7", "MU", "MU2", "C-3", "I-2"];
const PERMITS = ["Building", "Electrical", "Plumbing", "Mechanical", "Demo"];
const LIFESTYLE = [
  "narrow_lot",
  "aging_in_place",
  "first_time_buyer",
  "multi_generational",
  "accessory_dwelling",
  "family_starter",
  "courtyard_living",
];

const STEP_COUNT = 6;

const STEP_LABELS = [
  "About the design",
  "Specifications",
  "Cost data",
  "Images",
  "Parcel verification",
  "Review",
];

const EMPTY = {
  // 1
  name: "",
  typology: "",
  style: "",
  year_designed: "",
  // 2
  sqft: "",
  beds: "",
  baths: "",
  min_lot_width: "",
  zoning_fits: [],
  required_permits: [],
  lifestyle_tags: [],
  // 3
  historical_cost: "",
  historical_cost_year: "",
  soft_costs: "",
  // 4
  exterior_photo: null,
  floor_plan: null,
  additional_images: [],
  // 5
  parcel_verification: null, // { parcel, fits, note }
};

export default function DesignIntake() {
  const { user } = useRole();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const draftId = params.get("draft") || null;

  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(!!draftId);

  // Pre-fill the form when editing an existing Draft / Needs-Revision design.
  useEffect(() => {
    if (!draftId || !user?.id) return;
    let alive = true;
    setLoading(true);
    getMyDesign(draftId, user.id)
      .then((d) => {
        if (!alive) return;
        setDraft(d);
        if (d.draft_step && Number(d.draft_step) >= 1 && Number(d.draft_step) <= STEP_COUNT) {
          setStep(Number(d.draft_step));
        }
        setForm({
          name: d.name || "",
          typology: d.typology || "",
          style: d.style || "",
          year_designed: d.year_designed || "",
          sqft: d.sqft || "",
          beds: d.beds || "",
          baths: d.baths || "",
          min_lot_width: d.min_lot_width || "",
          zoning_fits: d.zoning_fits || [],
          required_permits: d.required_permits || [],
          lifestyle_tags: d.lifestyle_tags || [],
          historical_cost: d.historical_cost || "",
          historical_cost_year: d.historical_cost_year || "",
          soft_costs: d.soft_costs || "",
          exterior_photo: d.exterior_photo || null,
          floor_plan: d.floor_plan || null,
          additional_images: d.additional_images || [],
          parcel_verification: d.parcel_verification
            ? { parcel: { id: d.parcel_verification.parcel_id,
                          address: d.parcel_verification.parcel_address },
                fits: d.parcel_verification.fits,
                note: d.parcel_verification.note || "" }
            : null,
        });
      })
      .catch(() => alive && setSubmitError("Could not load this draft."))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [draftId, user?.id]);

  function set(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }
  function toggleArr(k, v) {
    setForm((f) => {
      const arr = f[k] || [];
      return { ...f, [k]: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v] };
    });
  }

  function validate(s) {
    const e = {};
    if (s === 1) {
      if (!form.name.trim()) e.name = "Design name is required.";
      if (!form.typology) e.typology = "Choose a typology.";
      if (!form.style) e.style = "Choose a style.";
    } else if (s === 2) {
      if (!form.sqft || Number(form.sqft) <= 0)
        e.sqft = "Square footage is required.";
      if (!form.beds) e.beds = "Bedrooms required.";
      if (!form.baths) e.baths = "Bathrooms required.";
      if (!form.min_lot_width || Number(form.min_lot_width) <= 0)
        e.min_lot_width = "Minimum lot width is required.";
      if (form.zoning_fits.length === 0)
        e.zoning_fits = "Select at least one zoning code.";
    } else if (s === 3) {
      if (!form.historical_cost || Number(form.historical_cost) <= 0)
        e.historical_cost = "Historical cost is required.";
      const yr = Number(form.historical_cost_year);
      if (!yr || yr < 1990 || yr > 2026)
        e.historical_cost_year = "Year between 1990 and 2026.";
    } else if (s === 4) {
      if (!form.exterior_photo) e.exterior_photo = "Exterior photo required.";
      if (!form.floor_plan) e.floor_plan = "Floor plan required.";
    } else if (s === 5) {
      if (!form.parcel_verification?.parcel)
        e.parcel_verification = "Select a reference parcel.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function next() {
    if (validate(step)) {
      const nextStep = Math.min(STEP_COUNT, step + 1);
      // Fire-and-forget autosave on every Next when editing a draft, so
      // partial progress survives a refresh/close before Step 6.
      if (draftId) void autosave(nextStep);
      setStep(nextStep);
    }
  }
  function prev() {
    setStep((s) => Math.max(1, s - 1));
  }

  // Build a partial payload from whatever the user has filled so far. Unlike
  // submit(), this never throws on missing required fields — autosave should
  // tolerate half-filled steps.
  function buildDraftPayload() {
    const p = {};
    if (form.name) p.name = form.name.trim();
    if (form.typology) p.typology = form.typology;
    if (form.style) p.style = form.style;
    if (form.year_designed) p.year_designed = Number(form.year_designed);
    if (form.sqft) p.sqft = Number(form.sqft);
    if (form.beds) p.beds = Number(form.beds);
    if (form.baths) p.baths = Number(form.baths);
    if (form.min_lot_width) p.min_lot_width = Number(form.min_lot_width);
    p.zoning_fits = form.zoning_fits || [];
    p.required_permits = form.required_permits || [];
    p.lifestyle_tags = form.lifestyle_tags || [];
    if (form.historical_cost) p.historical_cost = Number(form.historical_cost);
    if (form.historical_cost_year)
      p.historical_cost_year = Number(form.historical_cost_year);
    if (form.soft_costs !== "" && form.soft_costs !== null && form.soft_costs !== undefined)
      p.soft_costs = Number(form.soft_costs || 0);
    if (form.exterior_photo) p.exterior_photo = form.exterior_photo;
    if (form.floor_plan) p.floor_plan = form.floor_plan;
    if (form.additional_images?.length)
      p.additional_images = form.additional_images;
    if (form.parcel_verification?.parcel) {
      p.parcel_verification = {
        parcel_id: form.parcel_verification.parcel.id,
        fits: form.parcel_verification.fits,
        note: form.parcel_verification.note || "",
      };
    }
    return p;
  }

  const autosavingRef = useRef(false);
  const [savingDraft, setSavingDraft] = useState(false);
  async function autosave(nextStep) {
    if (!draftId || !user?.id || autosavingRef.current) return;
    autosavingRef.current = true;
    setSavingDraft(true);
    try {
      const payload = buildDraftPayload();
      // Remember where the architect was so the next session can resume here.
      payload.draft_step = nextStep ?? step;
      await updateMyDesign(draftId, user.id, payload, false);
    } catch {
      /* swallow — autosave is opportunistic and shouldn't block navigation */
    } finally {
      autosavingRef.current = false;
      setSavingDraft(false);
    }
  }

  async function saveAndExit() {
    if (!draftId) return;
    setSavingDraft(true);
    try {
      const payload = buildDraftPayload();
      payload.draft_step = step;
      await updateMyDesign(draftId, user.id, payload, false);
      toast.success(
        `Draft saved · v${draft?.version || 2} · resumes where you left off`,
      );
      navigate("/architect/designs");
    } catch (e) {
      toast.error("Could not save draft. Try again.");
      setSavingDraft(false);
    }
  }

  const adjusted2026 = useMemo(() => {
    const h = Number(form.historical_cost);
    const y = Number(form.historical_cost_year);
    if (!h || !y || y >= 2026) return h || 0;
    const years = Math.max(0, 2026 - y);
    const factor = Math.pow(1.03, years) * 1.06;
    return Math.round(h * factor);
  }, [form.historical_cost, form.historical_cost_year]);

  const planPurchase = useMemo(() => {
    const sqft = Number(form.sqft) || 0;
    return 2500 + Math.max(0, sqft);
  }, [form.sqft]);

  async function submit() {
    if (![1, 2, 3, 4, 5].every((s) => validate(s))) {
      toast.error("Please fix the highlighted fields before submitting.");
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      const payload = {
        name: form.name.trim(),
        typology: form.typology,
        style: form.style,
        year_designed: form.year_designed ? Number(form.year_designed) : null,
        sqft: Number(form.sqft),
        beds: Number(form.beds),
        baths: Number(form.baths),
        min_lot_width: Number(form.min_lot_width),
        zoning_fits: form.zoning_fits,
        required_permits: form.required_permits,
        lifestyle_tags: form.lifestyle_tags,
        historical_cost: Number(form.historical_cost),
        historical_cost_year: Number(form.historical_cost_year),
        soft_costs: Number(form.soft_costs || 0),
        exterior_photo: form.exterior_photo,
        floor_plan: form.floor_plan,
        additional_images: form.additional_images,
        parcel_verification: {
          parcel_id: form.parcel_verification.parcel.id,
          fits: form.parcel_verification.fits,
          note: form.parcel_verification.note || "",
        },
      };
      let newDesign;
      if (draftId) {
        newDesign = await updateMyDesign(draftId, user.id, payload, true);
      } else {
        newDesign = await submitDesign(user.id, payload);
      }
      toast.success(
        draftId
          ? `Version ${newDesign.version || 2} submitted for review.`
          : "Design submitted for review.",
      );
      navigate(`/architect/designs?just=${newDesign.id}`);
    } catch (e) {
      const detail =
        e?.response?.data?.detail || e?.message || "Could not submit.";
      setSubmitError(typeof detail === "string" ? detail : "Submit failed.");
    } finally {
      setSubmitting(false);
    }
  }

  // Breadcrumb label depends on whether we're creating a new design or editing
  // a draft version of an approved one. For drafts, surface the parent design
  // name + the version we're authoring.
  const crumbCurrent = draft
    ? `Editing ${draft.name || "design"} · v${draft.version || 2}`
    : "New design";

  return (
    <section
      data-testid="design-intake-page"
      className="animate-fade-in-up"
      style={{ maxWidth: 720, margin: "0 auto", paddingTop: 12 }}
    >
      <Toaster position="top-right" richColors closeButton />

      <BackBar
        parent={{ to: "/architect/designs", label: "Designs" }}
        trail={[
          { label: "Designs", to: "/architect/designs" },
          { label: crumbCurrent },
        ]}
        testId="intake-backbar"
      />

      {draft && (
        <VersionBanner draft={draft} />
      )}

      <Stepper
        step={step}
        total={STEP_COUNT}
        labels={STEP_LABELS}
        onJump={(n) => {
          // Allow jumping to any step the user has previously completed
          // (n <= step) without re-validation; forward jumps still require
          // validation via the Next button.
          if (n <= step) setStep(n);
        }}
      />

      {loading ? (
        <div
          style={{ color: "#6C6B85", fontStyle: "italic", padding: "32px 0" }}
        >
          Loading draft…
        </div>
      ) : (
      <div data-testid="intake-body">
        {step !== 6 && (
          <IntakePreviewStrip
            form={form}
            adjusted2026={adjusted2026}
            planPurchase={planPurchase}
            draft={draft}
          />
        )}
        <div style={{ minWidth: 0 }}>
        {step === 1 && <Step1 form={form} set={set} errors={errors} />}
        {step === 2 && (
          <Step2 form={form} set={set} toggleArr={toggleArr} errors={errors} />
        )}
        {step === 3 && (
          <Step3
            form={form}
            set={set}
            errors={errors}
            adjusted2026={adjusted2026}
            planPurchase={planPurchase}
          />
        )}
        {step === 4 && <Step4 form={form} set={set} errors={errors} />}
        {step === 5 && (
          <Step5
            form={form}
            set={set}
            userId={user?.id}
            errors={errors}
          />
        )}
        {step === 6 && (
          <Review
            form={form}
            adjusted2026={adjusted2026}
            planPurchase={planPurchase}
            onEdit={setStep}
          />
        )}

        {submitError && (
          <div
            data-testid="intake-error"
            className="mt-6 wn-card"
            style={{
              borderColor: "#A23B2E",
              backgroundColor: "rgba(162,59,46,0.06)",
              padding: "12px 16px",
              color: "#A23B2E",
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontSize: 13,
            }}
          >
            <AlertCircle size={16} />
            {submitError}
          </div>
        )}

        <div className="mt-10 flex items-center justify-between">
          <div className="flex items-center" style={{ gap: 10 }}>
            <button
              type="button"
              className="wn-btn wn-btn-tertiary"
              onClick={() => (step === 1 ? navigate("/architect/designs") : prev())}
              data-testid="intake-back-btn"
            >
              <ArrowLeft size={16} /> {step === 1 ? "Cancel" : "Back"}
            </button>
            {draftId && (
              <button
                type="button"
                className="wn-btn wn-btn-tertiary"
                onClick={saveAndExit}
                disabled={savingDraft}
                data-testid="intake-save-exit-btn"
                title="Save your progress and come back later."
              >
                {savingDraft ? "Saving…" : "Save & exit"}
              </button>
            )}
          </div>
          {step < STEP_COUNT ? (
            <button
              type="button"
              className="wn-btn wn-btn-primary"
              onClick={next}
              data-testid="intake-next-btn"
            >
              Next <ArrowRight size={16} />
            </button>
          ) : (
            <button
              type="button"
              className="wn-btn wn-btn-accent"
              onClick={submit}
              disabled={submitting}
              data-testid="intake-submit-btn"
            >
              {submitting
                ? "Submitting…"
                : draftId
                  ? `Submit v${draft?.version || 2} for review`
                  : "Submit design"}{" "}
              <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
      </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
function IntakePreviewStrip({ form, adjusted2026, planPurchase, draft }) {
  const name = (form.name || "").trim();
  const typo = humanTypologyLite(form.typology);
  const style = humanStyleLite(form.style);
  const specs = [];
  if (form.sqft) specs.push(`${Number(form.sqft).toLocaleString()} sqft`);
  if (form.beds) specs.push(`${form.beds}bd / ${form.baths || 0}ba`);
  // Don't render the strip until at least one identifying field is set.
  const hasContent = !!(name || typo || style || specs.length);
  if (!hasContent) return null;

  return (
    <div
      className="flex items-center flex-wrap"
      style={{
        gap: 14,
        padding: "10px 14px",
        marginBottom: 24,
        border: "1px solid #ECEBF1",
        background: "#FBFAFD",
        borderRadius: 4,
      }}
      data-testid="intake-preview-strip"
    >
      <Eye size={13} color="#6C6B85" />
      <span className="wn-label" style={{ color: "#6C6B85" }}>
        Live preview
      </span>
      <span
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontSize: 14,
          fontWeight: 600,
          color: "#1B1F4B",
        }}
      >
        {name || "Untitled design"}
      </span>
      {(draft && (draft.version || 2) > 1) && (
        <span
          style={{
            padding: "1px 7px",
            borderRadius: 999,
            background: "#E6E7F1",
            color: "#1B1F4B",
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
          }}
        >
          v{draft.version || 2}
        </span>
      )}
      {(typo || style) && (
        <span
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 13,
          }}
        >
          {[typo, style].filter(Boolean).join(" · ")}
        </span>
      )}
      {specs.length > 0 && (
        <span
          className="tabular-nums"
          style={{ color: "#272636", fontSize: 12.5, fontWeight: 600 }}
        >
          {specs.join(" · ")}
        </span>
      )}
      <span style={{ flex: 1 }} />
      {(adjusted2026 > 0 || planPurchase > 0) && (
        <span
          className="tabular-nums"
          style={{
            color: "#272636",
            fontSize: 12,
            fontFamily: '"Nunito Sans", sans-serif',
            fontWeight: 600,
          }}
        >
          {adjusted2026 > 0 && (
            <>
              <span style={{ color: "#9AA0C0", fontWeight: 500 }}>2026 cost </span>
              ${adjusted2026.toLocaleString()}
            </>
          )}
          {adjusted2026 > 0 && planPurchase > 0 && (
            <span style={{ margin: "0 8px", color: "#D9D8E4" }}>·</span>
          )}
          {planPurchase > 0 && (
            <>
              <span style={{ color: "#9AA0C0", fontWeight: 500 }}>Plan </span>
              ${planPurchase.toLocaleString()}
            </>
          )}
        </span>
      )}
    </div>
  );
}

function humanTypologyLite(t) {
  if (!t) return null;
  const m = {
    single_family: "Single family",
    multi_family: "Multi-family",
    adu: "ADU",
    duplex: "Duplex",
    townhouse: "Townhouse",
    cottage: "Cottage",
    studio: "Studio",
  };
  return m[t] || t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function humanStyleLite(s) {
  if (!s) return null;
  const m = {
    shed_modern: "Shed Modern",
    modern_adu: "Modern · ADU",
    modern: "Modern",
    traditional: "Traditional",
    craftsman: "Craftsman",
    farmhouse: "Farmhouse",
    contemporary: "Contemporary",
  };
  return m[s] || s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
function VersionBanner({ draft }) {
  const v = draft.version || 2;
  return (
    <div
      className="flex items-start"
      data-testid="version-banner"
      style={{
        gap: 12,
        padding: "12px 16px",
        marginBottom: 18,
        border: "1px solid #D9D8E4",
        borderLeft: "3px solid #B14E2F",
        background: "#FAF6EC",
        borderRadius: 4,
      }}
    >
      <AlertCircle
        size={16}
        color="#8a651f"
        style={{ flexShrink: 0, marginTop: 2 }}
      />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: "#1B1F4B", fontSize: 14 }}>
          Editing version {v} of <em>{draft.name}</em>
        </div>
        <div
          style={{
            color: "#6C6B85",
            fontSize: 13,
            marginTop: 2,
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
          }}
        >
          The previous approved version remains live in the marketplace until
          this version is approved. Submitting will return the design to
          “In Review” for admin approval.
        </div>
      </div>
    </div>
  );
}

function Stepper({ step, total, labels, onJump }) {
  return (
    <div
      className="flex items-start"
      style={{
        gap: 0,
        marginBottom: 36,
      }}
      data-testid="intake-progress"
    >
      {Array.from({ length: total }, (_, i) => i + 1).map((n, i) => {
        const isDone = step > n;
        const isCurrent = step === n;
        const clickable = n <= step;
        const bg = isCurrent ? "#1B1F4B" : isDone ? "#5B6196" : "#FBFAFD";
        const color = isCurrent || isDone ? "#FBFAFD" : "#9AA0C0";
        const isLast = i === total - 1;
        return (
          <React.Fragment key={n}>
            <div
              className="flex flex-col items-center text-center"
              style={{ flex: 1, minWidth: 0 }}
            >
              <button
                type="button"
                data-testid={`intake-dot-${n}`}
                onClick={() => clickable && onJump && onJump(n)}
                disabled={!clickable}
                aria-current={isCurrent ? "step" : undefined}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 999,
                  backgroundColor: bg,
                  color,
                  border: `1px solid ${
                    isCurrent ? "#1B1F4B" : isDone ? "#5B6196" : "#D9D8E4"
                  }`,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: clickable ? "pointer" : "default",
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                {isDone ? <Check size={13} strokeWidth={2.4} /> : n}
              </button>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: isCurrent ? 700 : 500,
                  color: isCurrent
                    ? "#1B1F4B"
                    : isDone
                      ? "#272636"
                      : "#9AA0C0",
                  marginTop: 7,
                  letterSpacing: "0.01em",
                  fontFamily: '"Nunito Sans", sans-serif',
                  lineHeight: 1.25,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  width: "100%",
                  paddingInline: 4,
                }}
              >
                {labels[i]}
              </span>
            </div>
            {!isLast && (
              <div
                aria-hidden
                style={{
                  height: 1,
                  flex: "0 1 24px",
                  alignSelf: "flex-start",
                  marginTop: 14,
                  backgroundColor: step > n ? "#5B6196" : "#D9D8E4",
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
function ProgressDots({ step, total }) {
  return (
    <div className="flex items-center gap-2 mb-8" data-testid="intake-progress">
      {Array.from({ length: total }, (_, i) => i + 1).map((n, i) => {
        const isDone = step > n;
        const isCurrent = step === n;
        const bg = isCurrent ? "#1B1F4B" : isDone ? "#5B6196" : "#FBFAFD";
        const color = isCurrent || isDone ? "#FBFAFD" : "#9AA0C0";
        return (
          <React.Fragment key={n}>
            <div
              data-testid={`intake-dot-${n}`}
              style={{
                width: 26,
                height: 26,
                borderRadius: 999,
                backgroundColor: bg,
                color,
                border: `1px solid ${
                  isCurrent ? "#1B1F4B" : isDone ? "#5B6196" : "#D9D8E4"
                }`,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 12,
              }}
            >
              {isDone ? <Check size={12} /> : n}
            </div>
            {i < total - 1 && (
              <div
                style={{
                  height: 1,
                  flex: 1,
                  backgroundColor: step > n ? "#5B6196" : "#D9D8E4",
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function StepHeader({ eyebrow, title, blurb }) {
  return (
    <div className="mb-7">
      <div className="wn-label" style={{ color: "#8F3C23" }}>
        {eyebrow}
      </div>
      <h1 className="mt-2" style={{ fontSize: 32, fontWeight: 600, lineHeight: 1.1 }}>
        {title}
      </h1>
      {blurb && (
        <p className="mt-3" style={{ color: "#6C6B85", fontSize: 15, lineHeight: 1.55 }}>
          {blurb}
        </p>
      )}
    </div>
  );
}

function Field({ label, error, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="wn-label" style={{ display: "block", marginBottom: 6 }}>
        {label}
      </span>
      {children}
      {error && (
        <span
          style={{
            display: "block",
            marginTop: 6,
            color: "#A23B2E",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {error}
        </span>
      )}
    </label>
  );
}

function SingleChipPicker({ options, value, onChange, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const isOn = value === opt.key;
        return (
          <button
            type="button"
            key={opt.key}
            onClick={() => onChange(opt.key)}
            data-testid={`${testidPrefix}-${opt.key}`}
            style={{
              padding: "7px 14px",
              borderRadius: 999,
              border: `1px solid ${isOn ? "#1B1F4B" : "#D9D8E4"}`,
              backgroundColor: isOn ? "#1B1F4B" : "#FBFAFD",
              color: isOn ? "#FBFAFD" : "#272636",
              fontWeight: 600,
              fontSize: 12,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              cursor: "pointer",
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function MultiChipPicker({ options, value, onToggle, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const k = typeof opt === "string" ? opt : opt.key;
        const label = typeof opt === "string" ? opt : opt.label;
        const isOn = (value || []).includes(k);
        return (
          <button
            type="button"
            key={k}
            onClick={() => onToggle(k)}
            data-testid={`${testidPrefix}-${k}`}
            style={{
              padding: "7px 14px",
              borderRadius: 999,
              border: `1px solid ${isOn ? "#1B1F4B" : "#D9D8E4"}`,
              backgroundColor: isOn ? "#1B1F4B" : "#FBFAFD",
              color: isOn ? "#FBFAFD" : "#272636",
              fontWeight: 600,
              fontSize: 12,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              cursor: "pointer",
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------
function Step1({ form, set, errors }) {
  return (
    <div data-testid="intake-step-1">
      <StepHeader
        eyebrow={`Step 1 of ${STEP_COUNT} — About the design`}
        title="Tell us about the design."
      />
      <div className="grid gap-5">
        <Field label="Design name *" error={errors.name} testid="field-name">
          <input
            className="wn-input"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            data-testid="intake-name"
          />
        </Field>
        <Field label="Typology *" error={errors.typology} testid="field-typology">
          <SingleChipPicker
            options={TYPOLOGIES}
            value={form.typology}
            onChange={(v) => set("typology", v)}
            testidPrefix="intake-typology"
          />
        </Field>
        <Field label="Architectural style *" error={errors.style} testid="field-style">
          <SingleChipPicker
            options={STYLES}
            value={form.style}
            onChange={(v) => set("style", v)}
            testidPrefix="intake-style"
          />
        </Field>
        <Field label="Original year designed" testid="field-year">
          <input
            className="wn-input"
            inputMode="numeric"
            value={form.year_designed}
            onChange={(e) =>
              set("year_designed", e.target.value.replace(/[^\d]/g, "").slice(0, 4))
            }
            data-testid="intake-year"
          />
        </Field>
      </div>
    </div>
  );
}

function Step2({ form, set, toggleArr, errors }) {
  return (
    <div data-testid="intake-step-2">
      <StepHeader
        eyebrow={`Step 2 of ${STEP_COUNT} — Specifications`}
        title="The shape of it."
      />
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Square footage *" error={errors.sqft} testid="field-sqft">
          <input
            className="wn-input tabular-nums"
            inputMode="numeric"
            value={form.sqft}
            onChange={(e) =>
              set("sqft", e.target.value.replace(/[^\d]/g, "").slice(0, 6))
            }
            data-testid="intake-sqft"
          />
        </Field>
        <Field label="Minimum lot width (ft) *" error={errors.min_lot_width} testid="field-mlw">
          <input
            className="wn-input tabular-nums"
            inputMode="numeric"
            value={form.min_lot_width}
            onChange={(e) =>
              set("min_lot_width", e.target.value.replace(/[^\d]/g, "").slice(0, 4))
            }
            data-testid="intake-mlw"
          />
        </Field>
        <Field label="Bedrooms *" error={errors.beds} testid="field-beds">
          <input
            className="wn-input tabular-nums"
            inputMode="numeric"
            value={form.beds}
            onChange={(e) =>
              set("beds", e.target.value.replace(/[^\d]/g, "").slice(0, 2))
            }
            data-testid="intake-beds"
          />
        </Field>
        <Field label="Bathrooms *" error={errors.baths} testid="field-baths">
          <input
            className="wn-input tabular-nums"
            inputMode="decimal"
            value={form.baths}
            onChange={(e) => set("baths", e.target.value.replace(/[^\d.]/g, "").slice(0, 4))}
            data-testid="intake-baths"
          />
        </Field>
      </div>
      <div className="mt-6">
        <Field
          label="Compatible zoning codes *"
          error={errors.zoning_fits}
          testid="field-zoning"
        >
          <MultiChipPicker
            options={ZONING}
            value={form.zoning_fits}
            onToggle={(v) => toggleArr("zoning_fits", v)}
            testidPrefix="intake-zoning"
          />
        </Field>
      </div>
      <div className="mt-6">
        <Field label="Required permits" testid="field-permits">
          <MultiChipPicker
            options={PERMITS}
            value={form.required_permits}
            onToggle={(v) => toggleArr("required_permits", v)}
            testidPrefix="intake-permit"
          />
        </Field>
      </div>
      <div className="mt-6">
        <Field label="Lifestyle tags" testid="field-lifestyle">
          <MultiChipPicker
            options={LIFESTYLE.map((k) => ({ key: k, label: k.replace(/_/g, " ") }))}
            value={form.lifestyle_tags}
            onToggle={(v) => toggleArr("lifestyle_tags", v)}
            testidPrefix="intake-lifestyle"
          />
        </Field>
      </div>
    </div>
  );
}

function Step3({ form, set, errors, adjusted2026, planPurchase }) {
  return (
    <div data-testid="intake-step-3">
      <StepHeader
        eyebrow={`Step 3 of ${STEP_COUNT} — Cost data`}
        title="What it costs to build."
      />
      <div className="grid sm:grid-cols-2 gap-5">
        <Field
          label="Historical hard construction cost *"
          error={errors.historical_cost}
          testid="field-hcost"
        >
          <input
            className="wn-input tabular-nums"
            inputMode="numeric"
            value={form.historical_cost}
            onChange={(e) =>
              set(
                "historical_cost",
                e.target.value.replace(/[^\d]/g, "").slice(0, 8)
              )
            }
            data-testid="intake-hcost"
            placeholder="e.g. 250000"
          />
        </Field>
        <Field
          label="Year costed *"
          error={errors.historical_cost_year}
          testid="field-hcost-year"
        >
          <input
            className="wn-input tabular-nums"
            inputMode="numeric"
            value={form.historical_cost_year}
            onChange={(e) =>
              set(
                "historical_cost_year",
                e.target.value.replace(/[^\d]/g, "").slice(0, 4)
              )
            }
            data-testid="intake-hcost-year"
            placeholder="e.g. 2022"
          />
        </Field>
      </div>
      <div className="mt-6 grid sm:grid-cols-2 gap-5">
        <DisplayCost
          label="Auto-computed 2026-adjusted cost"
          value={adjusted2026}
          testid="display-adjusted"
        />
        <DisplayCost
          label="Plan purchase price ($2,500 + $1 / sqft)"
          value={planPurchase}
          testid="display-purchase"
        />
      </div>
      <div className="mt-6">
        <Field label="Estimated soft costs" testid="field-soft">
          <input
            className="wn-input tabular-nums"
            inputMode="numeric"
            value={form.soft_costs}
            onChange={(e) =>
              set("soft_costs", e.target.value.replace(/[^\d]/g, "").slice(0, 8))
            }
            data-testid="intake-soft"
          />
        </Field>
      </div>
    </div>
  );
}

function DisplayCost({ label, value, testid }) {
  return (
    <div
      className="wn-card"
      style={{
        padding: "12px 14px",
        backgroundColor: "rgba(177,78,47,0.06)",
        borderColor: "#E2B8A8",
      }}
      data-testid={testid}
    >
      <div className="wn-label" style={{ color: "#8F3C23" }}>
        {label}
      </div>
      <div
        className="tabular-nums mt-1"
        style={{
          fontFamily: '"Nunito Sans", sans-serif',
          fontWeight: 700,
          color: "#8F3C23",
          fontSize: 22,
        }}
      >
        ${value.toLocaleString("en-US")}
      </div>
    </div>
  );
}

function Step4({ form, set, errors }) {
  return (
    <div data-testid="intake-step-4">
      <StepHeader
        eyebrow={`Step 4 of ${STEP_COUNT} — Images`}
        title="Show us the design."
        blurb="PDF, JPG, or PNG · up to 10 MB."
      />
      <UploadField
        label="Exterior photo *"
        purpose="exterior_photo"
        value={form.exterior_photo}
        onChange={(v) => set("exterior_photo", v)}
        testid="upload-exterior"
        error={errors.exterior_photo}
      />
      <div className="mt-5">
        <UploadField
          label="Basic floor plan *"
          purpose="floor_plan"
          value={form.floor_plan}
          onChange={(v) => set("floor_plan", v)}
          testid="upload-floor-plan"
          error={errors.floor_plan}
        />
      </div>
      <div className="mt-5">
        <MultiUploadField
          label="Additional images (optional · up to 4)"
          value={form.additional_images}
          onChange={(v) => set("additional_images", v)}
          max={4}
          testid="upload-additional"
        />
      </div>
    </div>
  );
}

function UploadField({ label, purpose, value, onChange, testid, error }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [local, setLocal] = useState("");

  async function onPick(file) {
    if (!file) return;
    setBusy(true);
    setLocal("");
    try {
      const meta = await uploadFile(file, purpose);
      onChange({
        id: meta.id,
        label,
        original_name: meta.original_name,
        mime: meta.mime,
        size: meta.size,
        url: meta.url,
      });
    } catch (e) {
      setLocal(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div data-testid={testid}>
      <div className="wn-label" style={{ marginBottom: 6 }}>
        {label}
      </div>
      {value ? (
        <UploadedRow
          file={value}
          onRemove={() => onChange(null)}
          testidPrefix={testid}
        />
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`${testid}-trigger`}
          style={{
            width: "100%",
            backgroundColor: "#FBFAFD",
            border: "1px dashed #9AA0C0",
            borderRadius: 10,
            padding: "20px 18px",
            display: "flex",
            alignItems: "center",
            gap: 14,
            cursor: busy ? "wait" : "pointer",
            color: "#1B1F4B",
            textAlign: "left",
          }}
        >
          <Upload size={20} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>
              {busy ? "Uploading…" : "Click to choose a file"}
            </div>
            <div style={{ fontSize: 12, color: "#6C6B85", marginTop: 2 }}>
              PDF, JPG, or PNG · up to 10 MB.
            </div>
          </div>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/jpeg,image/png"
        hidden
        onChange={(e) => onPick(e.target.files?.[0])}
      />
      {(local || error) && (
        <div
          style={{
            marginTop: 6,
            color: "#A23B2E",
            fontSize: 12,
            fontWeight: 600,
          }}
          data-testid={`${testid}-error`}
        >
          {local || error}
        </div>
      )}
    </div>
  );
}

function MultiUploadField({ label, value, onChange, max, testid }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [local, setLocal] = useState("");

  async function onPick(file) {
    if (!file) return;
    if (value.length >= max) {
      setLocal(`You can attach up to ${max} images.`);
      return;
    }
    setBusy(true);
    setLocal("");
    try {
      const meta = await uploadFile(file, "additional_image");
      onChange([
        ...value,
        {
          id: meta.id,
          label: "Additional image",
          original_name: meta.original_name,
          mime: meta.mime,
          size: meta.size,
          url: meta.url,
        },
      ]);
    } catch (e) {
      setLocal(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div data-testid={testid}>
      <div className="wn-label" style={{ marginBottom: 6 }}>
        {label}
      </div>
      <div className="space-y-2">
        {value.map((f, i) => (
          <UploadedRow
            key={f.id}
            file={f}
            onRemove={() => onChange(value.filter((_, idx) => idx !== i))}
            testidPrefix={`${testid}-${i}`}
          />
        ))}
      </div>
      {value.length < max && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`${testid}-trigger`}
          className="mt-2"
          style={{
            width: "100%",
            backgroundColor: "#FBFAFD",
            border: "1px dashed #9AA0C0",
            borderRadius: 10,
            padding: 16,
            cursor: busy ? "wait" : "pointer",
            color: "#1B1F4B",
            textAlign: "left",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <Upload size={18} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>
              {busy ? "Uploading…" : `Add image (${value.length}/${max})`}
            </div>
          </div>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/jpeg,image/png"
        hidden
        onChange={(e) => onPick(e.target.files?.[0])}
      />
      {local && (
        <div
          style={{ marginTop: 6, color: "#A23B2E", fontSize: 12, fontWeight: 600 }}
        >
          {local}
        </div>
      )}
    </div>
  );
}

function Step5({ form, set, userId, errors }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [note, setNote] = useState(form.parcel_verification?.note || "");

  async function doSearch() {
    if (!q.trim()) return;
    setSearching(true);
    try {
      const { items } = await searchParcels(userId, q.trim());
      setResults(items);
    } finally {
      setSearching(false);
    }
  }

  function pick(parcel) {
    const fits = checkFit(parcel, form);
    set("parcel_verification", { parcel, fits, note });
  }

  function updateNote(v) {
    setNote(v);
    if (form.parcel_verification) {
      set("parcel_verification", { ...form.parcel_verification, note: v });
    }
  }

  return (
    <div data-testid="intake-step-5">
      <StepHeader
        eyebrow={`Step 5 of ${STEP_COUNT} — Parcel verification`}
        title="Verify against a real parcel."
        blurb="Pick a reference parcel from the platform's inventory; we'll check fit against your specs."
      />
      <div className="flex gap-2 items-center">
        <div
          className="flex items-center gap-2 wn-card"
          style={{ padding: "0 12px", height: 40, flex: 1 }}
        >
          <Search size={14} style={{ color: "#9AA0C0" }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by address or parcel ID…"
            data-testid="intake-parcel-search"
            style={{
              border: 0,
              outline: 0,
              fontSize: 13,
              background: "transparent",
              width: "100%",
              fontFamily: '"Nunito Sans", sans-serif',
            }}
            onKeyDown={(e) => e.key === "Enter" && doSearch()}
          />
        </div>
        <button
          type="button"
          className="wn-btn wn-btn-primary"
          onClick={doSearch}
          disabled={searching}
          data-testid="intake-parcel-search-btn"
        >
          {searching ? "Searching…" : "Search"}
        </button>
      </div>

      <div className="mt-4 space-y-2" data-testid="intake-parcel-results">
        {results.map((p) => {
          const fits = checkFit(p, form);
          const isPicked = form.parcel_verification?.parcel?.id === p.id;
          return (
            <button
              type="button"
              key={p.id}
              onClick={() => pick(p)}
              data-testid={`intake-parcel-${p.id}`}
              className="wn-card text-left w-full transition-all"
              style={{
                padding: "12px 16px",
                display: "grid",
                gridTemplateColumns: "minmax(0,1.4fr) auto auto",
                gap: 12,
                alignItems: "center",
                borderColor: isPicked ? "#1B1F4B" : "#D9D8E4",
              }}
            >
              <div className="min-w-0">
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 14,
                    color: "#1B1F4B",
                  }}
                >
                  {p.address}
                </div>
                <div
                  className="tabular-nums"
                  style={{ fontSize: 12, color: "#6C6B85" }}
                >
                  {p.neighborhood} · {p.width_ft}′ × {p.depth_ft}′ · {p.zoning}
                </div>
              </div>
              <span
                style={{
                  padding: "3px 10px",
                  borderRadius: 999,
                  background: fits ? "rgba(79,97,73,0.12)" : "rgba(185,138,60,0.16)",
                  color: fits ? "#4F6149" : "#8a651f",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                }}
              >
                {fits ? "FITS" : "REVIEW"}
              </span>
              {isPicked && (
                <Check size={16} style={{ color: "#1B1F4B" }} />
              )}
            </button>
          );
        })}
      </div>

      {form.parcel_verification && (
        <div
          className="mt-5 wn-card"
          style={{
            padding: "14px 18px",
            borderLeft: `3px solid ${
              form.parcel_verification.fits ? "#4F6149" : "#B98A3C"
            }`,
            background: form.parcel_verification.fits
              ? "rgba(79,97,73,0.06)"
              : "rgba(185,138,60,0.06)",
          }}
          data-testid="intake-parcel-result"
        >
          <div
            style={{
              fontWeight: 700,
              color: form.parcel_verification.fits ? "#4F6149" : "#8a651f",
              fontSize: 14,
            }}
          >
            {form.parcel_verification.fits
              ? `✓ Verified compatible with ${form.parcel_verification.parcel.address}`
              : `! Not a standard fit at ${form.parcel_verification.parcel.address}`}
          </div>
          {!form.parcel_verification.fits && (
            <p style={{ marginTop: 4, color: "#6C6B85", fontSize: 12 }}>
              {fitReason(form.parcel_verification.parcel, form)}
            </p>
          )}
          <div className="mt-3">
            <div className="wn-label">Optional note</div>
            <textarea
              className="wn-input mt-1"
              style={{ minHeight: 70, padding: 10 }}
              placeholder="Acknowledge the non-standard fit, or describe the rationale."
              value={note}
              onChange={(e) => updateNote(e.target.value)}
              data-testid="intake-parcel-note"
            />
          </div>
        </div>
      )}

      {errors.parcel_verification && (
        <div
          style={{
            marginTop: 8,
            color: "#A23B2E",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {errors.parcel_verification}
        </div>
      )}
    </div>
  );
}

function checkFit(parcel, form) {
  if (!parcel || !form.min_lot_width) return false;
  const widthOk = (parcel.width_ft || 0) >= Number(form.min_lot_width);
  const zoningOk = (form.zoning_fits || []).includes(parcel.zoning);
  return widthOk && zoningOk;
}

function fitReason(parcel, form) {
  const reasons = [];
  if ((parcel.width_ft || 0) < Number(form.min_lot_width)) {
    reasons.push(
      `Lot width ${parcel.width_ft}′ is below your minimum (${form.min_lot_width}′).`
    );
  }
  if (!(form.zoning_fits || []).includes(parcel.zoning)) {
    reasons.push(`Parcel zoning ${parcel.zoning} is not in your compatible list.`);
  }
  return reasons.join(" ");
}

function Review({ form, adjusted2026, planPurchase, onEdit }) {
  return (
    <div data-testid="intake-review">
      <StepHeader
        eyebrow="Review — final check"
        title="Does this look right?"
        blurb="Submit to send your design to City Staff for review."
      />
      <Section title="About the design" step={1} onEdit={onEdit}>
        <KV label="Name">{form.name}</KV>
        <KV label="Typology">{form.typology}</KV>
        <KV label="Style">{form.style}</KV>
        <KV label="Year designed">{form.year_designed || "—"}</KV>
      </Section>
      <Section title="Specifications" step={2} onEdit={onEdit}>
        <KV label="Sqft">{form.sqft}</KV>
        <KV label="Min lot width">{form.min_lot_width}′</KV>
        <KV label="Beds / Baths">{form.beds} / {form.baths}</KV>
        <KV label="Zoning fits">{form.zoning_fits.join(", ")}</KV>
        <KV label="Permits">{form.required_permits.join(", ") || "—"}</KV>
        <KV label="Lifestyle">{form.lifestyle_tags.join(", ") || "—"}</KV>
      </Section>
      <Section title="Costs" step={3} onEdit={onEdit}>
        <KV label="Historical">${Number(form.historical_cost).toLocaleString("en-US")} ({form.historical_cost_year})</KV>
        <KV label="2026 adjusted">${adjusted2026.toLocaleString("en-US")}</KV>
        <KV label="Plan purchase">${planPurchase.toLocaleString("en-US")}</KV>
        <KV label="Soft costs">${Number(form.soft_costs || 0).toLocaleString("en-US")}</KV>
      </Section>
      <Section title="Images" step={4} onEdit={onEdit}>
        <div className="space-y-2 col-span-2">
          {form.exterior_photo && (
            <UploadedRow file={form.exterior_photo} testidPrefix="review-exterior" />
          )}
          {form.floor_plan && (
            <UploadedRow file={form.floor_plan} testidPrefix="review-plan" />
          )}
          {form.additional_images.map((f, i) => (
            <UploadedRow key={f.id} file={f} testidPrefix={`review-add-${i}`} />
          ))}
        </div>
      </Section>
      <Section title="Reference parcel" step={5} onEdit={onEdit}>
        <KV label="Address">{form.parcel_verification?.parcel?.address || "—"}</KV>
        <KV label="Verification">
          {form.parcel_verification?.fits ? "✓ Compatible" : "! Non-standard"}
        </KV>
        <div className="col-span-2">
          {form.parcel_verification?.note && (
            <>
              <div className="wn-label">Note</div>
              <p style={{ marginTop: 4, fontSize: 13, color: "#272636" }}>
                {form.parcel_verification.note}
              </p>
            </>
          )}
        </div>
      </Section>
    </div>
  );
}

function Section({ title, step, onEdit, children }) {
  return (
    <div
      className="wn-card mt-4"
      style={{ padding: "16px 20px" }}
      data-testid={`review-section-${step}`}
    >
      <div className="flex items-start justify-between">
        <h3
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 600,
            fontSize: 17,
            color: "#1B1F4B",
          }}
        >
          {title}
        </h3>
        <button
          type="button"
          className="wn-btn wn-btn-tertiary"
          onClick={() => onEdit(step)}
          data-testid={`review-edit-${step}`}
        >
          Edit
        </button>
      </div>
      <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mt-2">{children}</dl>
    </div>
  );
}

function KV({ label, children }) {
  return (
    <div>
      <dt className="wn-label">{label}</dt>
      <dd
        className="tabular-nums"
        style={{
          marginTop: 1,
          color: "#272636",
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        {children}
      </dd>
    </div>
  );
}
